import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, FileText, ArrowRight, LockKey, Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import CopyCodeButton from "../components/CopyCodeButton";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import SearchableSelect from "../components/SearchableSelect";
import CurrencySelect from "../components/CurrencySelect";
import OwnershipToggle from "../components/OwnershipToggle";
import OwnerCell from "../components/OwnerCell";
import ReassignOwnerDialog from "../components/ReassignOwnerDialog";
import {
    ShowArchivedToggle, ArchivedBadge, ArchiveRowButton, UnarchiveRowButton, useArchiveAction,
} from "../components/ArchiveControls";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PinnedScrollbar } from "../components/PinnedScrollbar";

export default function PurchaseOrders() {
    const tableScrollRef = useRef(null);
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    // Iter 51.2 — Archive UI is locked to the primary super-admin only.
    const isPrimaryAdmin = !!user?.is_primary_admin;
    const [searchParams, setSearchParams] = useSearchParams();
    const initialStatus = searchParams.get("status") || "";
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [clientFilter, setClientFilter] = useState("");
    const [textQuery, setTextQuery] = useState("");
    const [mine, setMine] = useState(false);
    const [open, setOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null);
    const [form, setForm] = useState({ client_id: "", po_number: "", remarks: "", currency: "INR" });
    const [submitting, setSubmitting] = useState(false);
    // Iter 51.1 — Archive
    const [showArchived, setShowArchived] = useState(false);
    const archive = useArchiveAction({
        entityType: "PO",
        archiveUrl: (row) => `/billing/po/${row.id}/archive`,
        unarchiveUrl: (row) => `/billing/po/${row.id}/unarchive`,
        onDone: () => load(),
        describe: (row) => (
            <>
                <div><span className="reedos-eyebrow">PO</span>{" "}
                    <span className="font-mono text-slate-900">{row.po_number}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Client</span>{" "}
                    <span className="text-slate-800">{row.client?.name || "—"}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Status</span>{" "}
                    <span className="text-slate-800">{row.is_final ? "Finalized" : "Draft"}</span></div>
            </>
        ),
    });

    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        // Iter C.2.0 — Search now matches on PO number (visible),
        // internal PO id (for handoff links / audit references) AND
        // client name resolved via the `clients` lookup. The previous
        // implementation only matched `po.po_number` + `po.client?.name`
        // but the list endpoint returns `client_id` not a nested
        // `client` object — so client-name search was silently broken.
        const clientById = new Map(clients.map((c) => [c.id, (c.name || "").toLowerCase()]));
        return items.filter((po) => {
            const num = (po.po_number || "").toLowerCase();
            const pid = (po.id || "").toLowerCase();
            const cliName = (po.client?.name || clientById.get(po.client_id) || "").toLowerCase();
            const clientPoNum = (po.client_po_number || "").toLowerCase();
            return num.includes(q) || pid.includes(q) || cliName.includes(q) || clientPoNum.includes(q);
        });
    }, [items, clients, textQuery]);

    const load = async () => {
        try {
            const params = { mine };
            if (statusFilter) params.status = statusFilter;
            if (clientFilter) params.client_id = clientFilter;
            if (showArchived) params.include_archived = true;
            const [pos, cls] = await Promise.all([
                api.get("/billing/po/list", { params }),
                api.get("/billing/client/list"),
            ]);
            setItems(pos.data.items || []);
            setClients(cls.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load POs"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, clientFilter, mine, showArchived]);

    useEffect(() => {
        const sp = new URLSearchParams(searchParams);
        if (statusFilter) sp.set("status", statusFilter); else sp.delete("status");
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_id) {
            toast.error("Pick a client first");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/billing/po/create", form);
            toast.success(`PO ${data.po_number} created`);
            setOpen(false);
            setForm({ client_id: "", po_number: "", remarks: "", currency: "INR" });
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create PO"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="po-list-page">
            <PageHeader
                eyebrow="Sales · Phase 1"
                title="Purchase Orders"
                description="Each PO can have multiple versions. Once finalized, the chosen version is locked and ready to invoice."
                actions={
                    <RoleGuard permission="po:write">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="new-po-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                                    <Plus size={16} weight="bold" /> New PO
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-display">New Purchase Order</DialogTitle>
                                    <DialogDescription>Creates draft V1. Add line items on the PO detail screen.</DialogDescription>
                                </DialogHeader>
                                <form onSubmit={submit} className="space-y-4" data-testid="po-form">
                                    <div>
                                        <Label className="text-xs font-semibold">Client</Label>
                                        <SearchableSelect
                                            options={clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))}
                                            value={form.client_id}
                                            onChange={(v) => setForm({ ...form, client_id: v })}
                                            placeholder="— Select client —"
                                            searchPlaceholder="Type to filter clients…"
                                            emptyText="No matching client."
                                            testId="po-client-select"
                                            className="mt-1.5"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold">PO number (optional)</Label>
                                        <Input
                                            value={form.po_number}
                                            onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                                            placeholder="Leave blank for auto-generated"
                                            className="mt-1.5 font-mono"
                                            data-testid="po-number-input"
                                        />
                                    </div>
                                    <CurrencySelect
                                        value={form.currency}
                                        onChange={(v) => setForm({ ...form, currency: v })}
                                        testid="po-currency-select"
                                    />
                                    <div>
                                        <Label className="text-xs font-semibold">Remarks (V1)</Label>
                                        <Textarea
                                            value={form.remarks}
                                            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                                            placeholder="Initial draft notes..."
                                            rows={2}
                                            className="mt-1.5"
                                            data-testid="po-remarks-input"
                                        />
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="po-submit-btn">
                                            {submitting ? "Creating..." : "Create PO"}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </RoleGuard>
                }
            />

            <div className="px-6 sm:px-8 py-8">
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="po-filters">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={textQuery}
                            onChange={(e) => setTextQuery(e.target.value)}
                            placeholder="Search by PO # or client…"
                            className="pl-8 h-9 text-sm"
                            data-testid="po-search-input"
                        />
                    </div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="po-status-filter"
                    >
                        <option value="">All status</option>
                        <option value="draft">Draft</option>
                        <option value="finalized">Finalized</option>
                    </select>
                    <div className="w-56">
                        <SearchableSelect
                            options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))]}
                            value={clientFilter}
                            onChange={setClientFilter}
                            placeholder="All clients"
                            searchPlaceholder="Filter clients…"
                            emptyText="No matching client."
                            testId="po-client-filter"
                            className="!h-9"
                        />
                    </div>
                    <OwnershipToggle value={mine} onChange={setMine} testId="po-ownership-toggle" />
                    {isPrimaryAdmin && (
                        <ShowArchivedToggle
                            value={showArchived}
                            onChange={setShowArchived}
                            count={items.filter((p) => p.deleted_at).length}
                            testid="po-show-archived"
                        />
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500">Loading purchase orders...</div>
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<FileText size={40} weight="thin" />}
                        title="No purchase orders match"
                        description="Create your first PO or change the filters above."
                        data-testid="po-empty"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <div className="overflow-x-auto" ref={tableScrollRef} data-testid="po-table-scroll">
                        <table className="w-full text-sm" data-testid="po-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">PO number</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Versions</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Owner</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Created</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((po) => {
                                    const finalV = po.versions.find((v) => v.is_final_version);
                                    const latest = po.versions.reduce((a, b) => (a.version_number > b.version_number ? a : b));
                                    return (
                                        <tr key={po.id} className={`border-t border-slate-100 reedos-row-hover ${po.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`po-row-${po.id}`}>
                                            <td className="px-5 py-3">
                                                <span className="inline-flex items-center flex-wrap gap-1.5">
                                                    <Link to={`/purchase-orders/${po.id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                        {po.po_number}
                                                    </Link>
                                                    <CopyCodeButton
                                                        code={po.po_number}
                                                        label="PO number"
                                                        testid={`po-number-copy-${po.id}`}
                                                    />
                                                    {po.deleted_at && <ArchivedBadge testid={`po-archived-badge-${po.id}`} />}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-slate-900">{po.client?.name}</td>
                                            <td className="px-5 py-3">
                                                <div className="text-xs text-slate-700">
                                                    {po.versions.length} version{po.versions.length > 1 ? "s" : ""}
                                                    {finalV && <span className="text-amber-700 font-semibold ml-1">· final V{finalV.version_number}</span>}
                                                    {!finalV && <span className="text-slate-500 ml-1">· latest V{latest.version_number}</span>}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3">
                                                {po.is_final ? (
                                                    <StatusBadge status="Locked" icon={<LockKey size={10} weight="fill" />} />
                                                ) : (
                                                    <StatusBadge status="Draft" />
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                <OwnerCell
                                                    owner={po.owner}
                                                    testId={`po-owner-${po.id}`}
                                                    onReassignClick={isSuperAdmin ? () => setReassignTarget(po) : undefined}
                                                />
                                            </td>
                                            <td className="px-5 py-3 text-xs text-slate-500">{formatDate(po.created_date)}</td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="inline-flex items-center gap-2 justify-end">
                                                    {isPrimaryAdmin && (
                                                        po.deleted_at
                                                            ? <UnarchiveRowButton onClick={() => archive.openUnarchive(po)} testid={`po-unarchive-${po.id}`} />
                                                            : <ArchiveRowButton onClick={() => archive.openArchive(po)} testid={`po-archive-${po.id}`} />
                                                    )}
                                                    <Link to={`/purchase-orders/${po.id}`} className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-xs font-medium" data-testid={`po-open-${po.id}`}>
                                                        Open <ArrowRight size={12} weight="bold" />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                        <PinnedScrollbar targetRef={tableScrollRef} testid="po-pinned-scrollbar" />
                    </div>
                )}
            </div>
            <ReassignOwnerDialog
                open={Boolean(reassignTarget)}
                onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
                entityLabel="Purchase Order"
                entityName={reassignTarget?.po_number}
                currentOwner={reassignTarget?.owner}
                reassignUrl={reassignTarget ? `/billing/po/${reassignTarget.id}/owner` : ""}
                onSuccess={load}
                testIdPrefix="po-reassign"
            />
            {/* Iter 51.1 — Archive/Unarchive confirm dialog */}
            {archive.dialog}
        </div>
    );
}
